/* eslint-disable @typescript-eslint/no-unused-vars */
import {format} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {TypeLocalNameEnum} from 'domain/usecases';
import {makeGetHealthProviderNames} from 'main/factories/usecases/private/booking-flow/remote-health-booking-v1/get-health-provider-names-factory';
import {DrawerContentListByCategorySwitchProps} from 'presentation/components/Drawer/types';
import {DisabilityEnum, OperationStateTypesEnum, QueryParamsEnum} from 'presentation/constants';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {translations} from 'presentation/translations-new';
import {useEffect, useState} from 'react';
import {useRecoilValueLoadable} from 'recoil';
import {useQueryParams} from '../../../hooks';

import {FlowEnum, ShiftTypeEnum} from '../../constants';
import {bookingDatesState} from '../../pages/ProfessionalSelection/atoms';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';

import {useExternalOpenDateModalStore} from 'presentation/modules/booking/shared/store/useExternalOpenDateModalStore';

import {useDisabledSelectionDateStore} from 'presentation/modules/booking/shared/store/useDisabledSelectionDateStore';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {
	MAX_DISTANCE_FILTER_UNITS,
	MAX_DISTANCE_FILTER_UNITS_ODONTO,
} from '../useBookingUnits/constants';
import {GeolocationFilterType, UseSearchBarProps} from './types';
import {useProviderNamesStore} from 'presentation/modules/booking/shared/store/useProviderNamesStore';
import {useDistanceSliderStore} from 'presentation/modules/booking/shared/ui/components/DistanceSlider/store';
import {MIN_DISTANCE_IN_KM_HEALTH} from 'presentation/modules/booking/health/ui/components/HealthSearchBar/hooks/useHealthSearchBar/constants';
import {MANUAL_DISTANCE_ORIGIN} from 'presentation/modules/booking/shared/constants/unitsInfo';

const {afternoon, morning, night} = translations['pt-br'].privateFlow.bookingFlow.professionalFlow;
const {disabilityAttendance, all, period, pcdService} = translations['pt-br'].commons;

export const useSearchBar = ({activeStep, flowType}: UseSearchBarProps) => {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {SEARCH_PARAMS, setSearchParams, clearSearch} = useQueryParams();
	const [providerNamesOperationState, setProviderNamesOperationState] = useState(
		OperationStateTypesEnum.NONE,
	);

	const getProviderNamesState = useProviderNamesStore(state => state.providersName);
	const setGetProviderNamesState = useProviderNamesStore(state => state.setProvidersName);

	const {
		cityName,
		dateAt,
		districtName,
		unitCode,
		specialtyCode,
		subSpecialtyCode,
		ufCode,
		bookingNumber,
		isRebooking,
		typeLocalName,
		isManualLocation,
	} = bookingData;

	const [districtGeolocationState, setDistrictGeolocationState] = useState('');
	const [selectedFilterGeolocationState, setSelectedFilterGeolocationState] =
		useState<GeolocationFilterType>('distance');

	const {query, shift, disability, localityDistance, autocompleteQuery} = SEARCH_PARAMS;

	const disabledSelectionDate = useDisabledSelectionDateStore(
		state => state.disabledSelectionDate,
	);
	const setExternalOpenDateModal = useExternalOpenDateModalStore(
		state => state.setExternalOpenDateModal,
	);
	const openDrawerState = useOpenDrawerFilterStore(state => state.openDrawerState);
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);
	const distanceSlider = useDistanceSliderStore(state => state.distanceSlider);
	const setDistanceSlider = useDistanceSliderStore(state => state.setDistanceSlider);
	const [shiftSelected, setShiftSelected] = useState<ShiftTypeEnum>(
		(shift as ShiftTypeEnum) ?? ShiftTypeEnum.ALL,
	);
	const [disabilityCheck, setDisabilityCheck] = useState(
		disability === DisabilityEnum.HAS_ACCESSIBILITY,
	);

	const isDisabilityFilterActive = disability === DisabilityEnum.HAS_ACCESSIBILITY;

	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isHealth = flowType === FlowEnum.HEALTH;
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isExam = flowType === FlowEnum.EXAM;
	const isHealthOrExam = isHealth || isExam;
	const isHealthOrExamOrOdonto = isHealth || isExam || isOdonto;

	const isTeleOrOdonto = isTelehealth || isOdonto;
	const isHealthOrTelehealth = isTelehealth || isHealth;

	const {PROFESSIONAL_STEP, UNIT_STEP, SPECIALITY_OR_EXAM_STEP, DISTRICT_STEP} = getStepPosition({
		flowType,
		isManualLocation: bookingData.isManualLocation,
	});

	const showDateWhenTelehealth = isTelehealth && activeStep === PROFESSIONAL_STEP;
	const showDateWhenOdonto = isOdonto && activeStep === PROFESSIONAL_STEP;
	const showDateWhenOthers = !isTeleOrOdonto && activeStep === PROFESSIONAL_STEP;
	const showProfessionalFilters =
		showDateWhenTelehealth || showDateWhenOdonto || showDateWhenOthers;

	const showOdontoDrawer = isOdonto && activeStep === UNIT_STEP && Boolean(isManualLocation);

	const showFilterByLetter = !query && activeStep === SPECIALITY_OR_EXAM_STEP;

	const IS_PROVIDER_NAMES_ERROR = providerNamesOperationState === OperationStateTypesEnum.ERROR;
	const IS_PROVIDER_NAMES_DONE = providerNamesOperationState === OperationStateTypesEnum.DONE;
	const IS_PROVIDER_NAMES_SUBMITTING =
		providerNamesOperationState === OperationStateTypesEnum.SUBMITTING;

	const availableDatesParams = {
		cityName: isTelehealth ? 'FORTALEZA' : cityName,
		disability: disability as DisabilityEnum,
		districtName: isOdonto ? bookingData.odontoUnitDistrictSelected : bookingData.districtName,
		flowType,
		unitCode,
		specialtyCode,
		subSpecialtyCode,
		ufCode: isTelehealth ? 'CE' : ufCode,
		appointmentNumber: isTelehealth && isRebooking ? bookingNumber : undefined,
		typeLocalName: isManualLocation ? typeLocalName : TypeLocalNameEnum.CIDADE,
		providerId:
			(bookingData?.isFavoriteDoctorsFlow && bookingData?.professionalCode) ||
			autocompleteQuery,
	};

	async function getProviderNames() {
		try {
			setProviderNamesOperationState(OperationStateTypesEnum.SUBMITTING);
			const response = await makeGetHealthProviderNames().get({
				cdEspecialidade: Number(specialtyCode),
				cdPrestadorJuridico: Number(unitCode),
				cdSubespecialidade: Number(subSpecialtyCode),
				nmPrestadorFisico: null,
			});

			setGetProviderNamesState(response);
			setProviderNamesOperationState(OperationStateTypesEnum.DONE);
		} catch (error) {
			setProviderNamesOperationState(OperationStateTypesEnum.ERROR);
		}
	}

	const availableDatesList = useRecoilValueLoadable(bookingDatesState(availableDatesParams));

	const showDistanceDrawer = Boolean(isHealthOrExam && activeStep === UNIT_STEP);
	const showGeolocationFilterDrawer = !isManualLocation && isOdonto && activeStep === UNIT_STEP;
	const distanceFilterIsActive = localityDistance && localityDistance !== '0';
	const districtGeolocationFilterIsActive =
		!isManualLocation && districtName && activeStep === UNIT_STEP;
	const showFilter = showDistanceDrawer || showOdontoDrawer || showGeolocationFilterDrawer;
	const filterIsActive =
		isDisabilityFilterActive ||
		(isHealthOrExamOrOdonto && distanceFilterIsActive) ||
		(isHealthOrExamOrOdonto && districtGeolocationFilterIsActive);
	const distanceFilterAtMaxDistance =
		Number(localityDistance) >=
		(isOdonto ? MAX_DISTANCE_FILTER_UNITS_ODONTO : MAX_DISTANCE_FILTER_UNITS);

	function handleChangeDate(selectedDate: Date | null) {
		setSearchParams(prev => {
			if (selectedDate) {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: format(selectedDate, 'P', {locale: ptBR}),
				}));

				prev.set(QueryParamsEnum.CALENDAR_ACTIVE, 'true');
			} else {
				setBookingData(prevValue => ({
					...prevValue,
					dateAt: '',
				}));

				prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			}

			return prev;
		});
		setExternalOpenDateModal(false);
	}

	function handleShiftButton(shiftType: ShiftTypeEnum | string) {
		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.SHIFT, shiftType);
				return prev;
			},
			{
				replace: true,
			},
		);
	}

	const PERIODS = {
		categoryTitle: period,
		categoryItemsList: [
			{
				active: shiftSelected === ShiftTypeEnum.ALL,
				title: all,
				onClick: () => setShiftSelected(ShiftTypeEnum.ALL),
			},
			{
				active: shiftSelected === ShiftTypeEnum.MORNING,
				title: morning,
				onClick: () => setShiftSelected(ShiftTypeEnum.MORNING),
			},
			{
				active: shiftSelected === ShiftTypeEnum.AFTERNOON,
				title: afternoon,
				onClick: () => setShiftSelected(ShiftTypeEnum.AFTERNOON),
			},
			{
				active: shiftSelected === ShiftTypeEnum.NIGHT,
				title: night,
				onClick: () => setShiftSelected(ShiftTypeEnum.NIGHT),
			},
		],
	};

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const handleCloseDrawer = (abandon?: boolean) => {
		setOpenDrawerState(false);
		if (abandon) {
			setShiftSelected(ShiftTypeEnum.ALL);
			setDistanceSlider(Number(localityDistance));

			if (isDisabilityFilterActive) {
				setDisabilityCheck(true);
			} else {
				setDisabilityCheck(false);
			}

			if (districtGeolocationFilterIsActive) {
				setSelectedFilterGeolocationState('district');
				setDistrictGeolocationState(bookingData.districtName ?? '');
			} else {
				setSelectedFilterGeolocationState('distance');
				setDistrictGeolocationState('');
			}

			if (distanceFilterIsActive) {
				setSelectedFilterGeolocationState('distance');
			}
		}
	};

	useEffect(() => {
		if (SEARCH_PARAMS.shift === '') {
			handleCloseDrawer(!openDrawerState);
		}
	}, [SEARCH_PARAMS.shift]);

	function clearSearchParams() {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_QUERY);
				prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);

				return prev;
			},
			{
				replace: true,
			},
		);
	}

	const handleClearShiftAndSearch = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
		clearSearchParams();
		if (clearSearch) {
			clearSearch();
		}
		setSearchParams(prev => {
			setBookingData(prevValue => ({
				...prevValue,
				dateAt: '',
			}));

			prev.set(QueryParamsEnum.SEARCH_QUERY, 'clear');
			prev.delete(QueryParamsEnum.CALENDAR_ACTIVE);
			if (autocompleteQuery) prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
			return prev;
		});
	};

	const handleClearShift = () => {
		setShiftSelected(ShiftTypeEnum.ALL);
		handleShiftButton(ShiftTypeEnum.ALL);
		handleCloseDrawer();
	};

	const handleConfirmShift = () => {
		handleShiftButton(shiftSelected);
		handleCloseDrawer();
	};

	const handleCheck = (checked: boolean) => {
		setSearchParams(
			prev => {
				prev.set(
					QueryParamsEnum.DISABILITY,
					checked ? DisabilityEnum.HAS_ACCESSIBILITY : DisabilityEnum.ALL,
				);
				return prev;
			},
			{
				replace: true,
			},
		);
	};

	const SERVING_PEOPLE_WITH_DISABILITIES: DrawerContentListByCategorySwitchProps = {
		categoryTitle: pcdService,
		categoryItemsList: [
			{
				check: disabilityCheck,
				title: disabilityAttendance,
				onCheck: () => setDisabilityCheck(state => !state),
				icon: 'accessibility-filled',
			},
		],
	};

	const handleClearDisability = () => {
		setDisabilityCheck(false);
		handleCheck(false);
		handleCloseDrawer();
	};

	const handleConfirmDisability = () => {
		handleCheck(disabilityCheck);
		handleCloseDrawer();
	};

	const handleClearDistanceOdonto = () => {
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.DISTANCE_ORIGIN);
			return prev;
		});
		setDistanceSlider(0);
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.LOCALITY_DISTANCE, '0');
			return prev;
		});
		handleCloseDrawer();
	};

	const handleClearDistance = () => {
		if (isOdonto) {
			handleClearDistanceOdonto();
			return;
		}

		setDistanceSlider(25);
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.DISTANCE_ORIGIN);

			if (isManualLocation) {
				prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
			} else {
				prev.set(QueryParamsEnum.LOCALITY_DISTANCE, MIN_DISTANCE_IN_KM_HEALTH);
			}
			return prev;
		});
		handleCloseDrawer();
	};

	const handleConfirmDistance = () => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.LOCALITY_DISTANCE, distanceSlider.toString());
			prev.set(QueryParamsEnum.DISTANCE_ORIGIN, MANUAL_DISTANCE_ORIGIN);
			return prev;
		});

		handleCloseDrawer();
	};

	const shouldClearShiftFilter =
		(isTelehealth && activeStep === SPECIALITY_OR_EXAM_STEP) ||
		(isHealthOrExamOrOdonto && activeStep === UNIT_STEP);

	const shouldGetProviders = activeStep === PROFESSIONAL_STEP && (isTelehealth || isHealth);
	const shouldClearDisability =
		isOdonto &&
		((activeStep === DISTRICT_STEP && isManualLocation) ||
			(activeStep === SPECIALITY_OR_EXAM_STEP && !isManualLocation));

	useEffect(() => {
		if (shouldClearDisability) {
			handleClearDisability();
		}
		if (isOdonto && !isManualLocation && activeStep === SPECIALITY_OR_EXAM_STEP) {
			setDistrictGeolocationState('');
			setSelectedFilterGeolocationState('distance');
		}
		if (shouldClearShiftFilter) {
			handleClearShift();
		}
		if (shouldGetProviders) {
			getProviderNames();
		}
	}, [activeStep]);

	return {
		openDrawerState,
		handleOpenDrawer,
		handleCloseDrawer,
		disabledSelectionDate,
		showProfessionalFilters,
		showOdontoDrawer,
		showFilterByLetter,
		availableDatesList,
		handleChangeDate,
		PERIODS,
		SERVING_PEOPLE_WITH_DISABILITIES,
		handleConfirmShift,
		handleClearShift,
		handleClearShiftAndSearch,
		handleClearDisability,
		handleConfirmDisability,
		dateAt,
		shift,
		disabilityCheck,
		showFilter,
		filterIsActive,
		showDistanceDrawer,
		handleClearDistance,
		handleConfirmDistance,
		getProviderNames,
		getProviderNamesState,
		IS_PROVIDER_NAMES_ERROR,
		IS_PROVIDER_NAMES_DONE,
		IS_PROVIDER_NAMES_SUBMITTING,
		isTelehealth,
		isHealth,
		isHealthOrTelehealth,
		distanceFilterAtMaxDistance,
		showGeolocationFilterDrawer,
		districtGeolocationState,
		setDistrictGeolocationState,
		selectedFilterGeolocationState,
		setSelectedFilterGeolocationState,
		districtGeolocationFilterIsActive,
		isDisabilityFilterActive,
		isFavoriteDoctorsFlow: Boolean(bookingData?.isFavoriteDoctorsFlow),
	};
};
